import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import PageScaffold from '../components/PageScaffold';
import Lucide from '../components/midone/components/Base/Lucide';
import Tippy from '../components/midone/components/Base/Tippy';
import { fetchDashboardOverview, DashboardOverviewResponse, fetchNextDueAbandoned, fetchNextDueOrderItems, NextDueRow } from '../api/dashboard';
import Chart from '../components/midone/components/Base/Chart';
import { getColor } from '../components/midone/utils/colors';
import PaginationBar from '../components/PaginationBar';
import { Appointment, fetchAppointments } from '../api/appointments';

const Dashboard: FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [abandoned, setAbandoned] = useState<NextDueRow[]>([]);
  const [abandonedPage, setAbandonedPage] = useState(1);
  const [abandonedTotalPages, setAbandonedTotalPages] = useState(1);
  const [orderItems, setOrderItems] = useState<NextDueRow[]>([]);
  const [selectedDue, setSelectedDue] = useState<NextDueRow | null>(null);
  const [orderItemsPage, setOrderItemsPage] = useState(1);
  const [orderItemsTotalPages, setOrderItemsTotalPages] = useState(1);
  const [todayAppointments, setTodayAppointments] = useState<Appointment[]>([]);
  const [todayAppointmentsPage, setTodayAppointmentsPage] = useState(1);
  const [todayAppointmentsTotalPages, setTodayAppointmentsTotalPages] =
    useState(1);
  const [tomorrowAppointments, setTomorrowAppointments] = useState<
    Appointment[]
  >([]);
  const [tomorrowAppointmentsPage, setTomorrowAppointmentsPage] = useState(1);
  const [tomorrowAppointmentsTotalPages, setTomorrowAppointmentsTotalPages] =
    useState(1);

  const refreshAll = async () => {
    setLoading(true);
    try {
      const [overview, a, oi] = await Promise.all([
        fetchDashboardOverview(),
        fetchNextDueAbandoned(abandonedPage, 10),
        fetchNextDueOrderItems(orderItemsPage, 10),
      ]);
      setData(overview);
      setAbandoned(a.data);
      setAbandonedTotalPages(a.meta.totalPages);
      setOrderItems(oi.data);
      setOrderItemsTotalPages(oi.meta.totalPages);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to refresh');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(null);
    fetchDashboardOverview()
      .then((resp) => { if (!ignore) setData(resp); })
      .catch((e) => { if (!ignore) setError(e instanceof Error ? e.message : 'Failed to load dashboard'); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    let ignore = false;
    fetchNextDueAbandoned(abandonedPage, 10).then((resp) => {
      if (ignore) return;
      setAbandoned(resp.data);
      setAbandonedTotalPages(resp.meta.totalPages);
    }).catch(() => {});
    return () => { ignore = true; };
  }, [abandonedPage]);

  useEffect(() => {
    let ignore = false;
    fetchNextDueOrderItems(orderItemsPage, 10).then((resp) => {
      if (ignore) return;
      setOrderItems(resp.data);
      setOrderItemsTotalPages(resp.meta.totalPages);
    }).catch(() => {});
    return () => { ignore = true; };
  }, [orderItemsPage]);

  const loadAppointmentsForRange = async (
    setList: (items: Appointment[]) => void,
    setTotalPages: (v: number) => void,
    page: number,
    dayOffset: number,
  ) => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    const from = new Date(base);
    from.setDate(base.getDate() + dayOffset);
    const to = new Date(from);
    to.setHours(23, 59, 59, 999);

    const resp = await fetchAppointments({
      page,
      limit: 5,
      from: from.toISOString(),
      to: to.toISOString(),
    });
    // Server returns ASC; reverse here for descending
    const reversed = [...resp.data].sort(
      (a, b) =>
        new Date(b.scheduledAt).getTime() -
        new Date(a.scheduledAt).getTime(),
    );
    setList(reversed);
    setTotalPages(resp.meta.totalPages ?? 1);
  };

  useEffect(() => {
    void loadAppointmentsForRange(
      setTodayAppointments,
      setTodayAppointmentsTotalPages,
      todayAppointmentsPage,
      0,
    );
  }, [todayAppointmentsPage]);

  useEffect(() => {
    void loadAppointmentsForRange(
      setTomorrowAppointments,
      setTomorrowAppointmentsTotalPages,
      tomorrowAppointmentsPage,
      1,
    );
  }, [tomorrowAppointmentsPage]);

  const cards = useMemo(() => {
    const s = data?.statusCounts || {} as any;
    const statuses: Array<{ key: string; label: string }> = [
      { key: 'draft', label: 'Draft' },
      { key: 'approved', label: 'Approved' },
      { key: 'confirmed', label: 'Confirmed' },
      { key: 'in_progress', label: 'In progress' },
      { key: 'completed', label: 'Completed' },
      { key: 'verified', label: 'Verified' },
      { key: 'delivered', label: 'Delivered' },
      { key: 'cancelled', label: 'Cancelled' },
    ];
    return statuses.map(({ key, label }) => ({ label, value: s[key] || 0 }));
  }, [data]);

  const formatAmount = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const diffInfo = (todayValue = 0, yesterdayValue = 0) => {
    const today = Number(todayValue) || 0;
    const yest = Number(yesterdayValue) || 0;
    const diff = today - yest;
    const pct = yest === 0 ? (today > 0 ? 100 : 0) : (diff / yest) * 100;
    const isUp = diff >= 0;
    return { diff, pct, isUp };
  };

  return (
    <PageScaffold
      title="Dashboard"
      subtitle="At-a-glance orders and revenue performance."
      showDivider={false}
      actionsPosition="right"
      actions={
        <button
          type="button"
          onClick={refreshAll}
          className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:border-primary/30 hover:text-primary"
          title="Refresh"
        >
          <Lucide icon="RefreshCcw" className="h-4 w-4" />
          Refresh
        </button>
      }
    >
      {loading ? (
        <div className="text-slate-500">Loading overview…</div>
      ) : error ? (
        <div className="text-danger">{error}</div>
      ) : (
        <>
        <div className="flex flex-col gap-6">
          {/* Top KPI cards */}
          <div className="grid grid-cols-12 gap-4">
            {/* Total Orders KPI (Midone style) */}
            <div className="col-span-12 sm:col-span-6 xl:col-span-6 intro-y">
              <div
                className={clsx([
                  'relative zoom-in',
                  "before:box before:absolute before:inset-x-3 before:mt-3 before:h-full before:bg-slate-50 before:content-['']",
                ])}
              >
                <div className="p-5 box">
                  <div className="flex">
                    <Lucide icon="ShoppingCart" className="w-[28px] h-[28px] text-primary" />
                    <div className="ml-auto">
                      {(() => {
                        const i = diffInfo(data?.totals.todayOrders, data?.totals.yesterdayOrders);
                        const pct = Math.abs(i.pct).toFixed(1);
                        const tone = i.isUp ? 'bg-success' : 'bg-danger';
                        const label = `${pct}% ${i.isUp ? 'Higher' : 'Lower'} than yesterday`;
                        return (
                          <Tippy
                            as="div"
                            className={`cursor-pointer ${tone} py-[3px] flex rounded-full text-white text-xs pl-2 pr-1 items-center font-medium`}
                            content={label}
                          >
                            {pct}%
                            <Lucide icon={i.isUp ? 'ChevronUp' : 'ChevronDown'} className="w-4 h-4 ml-0.5" />
                          </Tippy>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="mt-6 text-3xl font-medium leading-8">{data?.totals.todayOrders ?? 0}</div>
                  <div className="mt-1 text-base text-slate-500">Total Orders (Today)</div>
                  <div className="mt-1 text-xs text-slate-500">Yesterday: <span className="font-semibold text-slate-700">{data?.totals.yesterdayOrders ?? 0}</span></div>
                </div>
              </div>
              </div>

            {/* Total Revenue KPI (Midone style) */}
            <div className="col-span-12 sm:col-span-6 xl:col-span-6 intro-y">
              <div
                className={clsx([
                  'relative zoom-in',
                  "before:box before:absolute before:inset-x-3 before:mt-3 before:h-full before:bg-slate-50 before:content-['']",
                ])}
              >
                <div className="p-5 box relative">
                  <div className="flex">
                    <Lucide icon="Wallet" className="w-[28px] h-[28px] text-primary" />
                    <div className="ml-auto">
                      {(() => {
                        const i = diffInfo(data?.totals.todayRevenue, data?.totals.yesterdayRevenue);
                        const pct = Math.abs(i.pct).toFixed(1);
                        const tone = i.isUp ? 'bg-success' : 'bg-danger';
                        const label = `${pct}% ${i.isUp ? 'Higher' : 'Lower'} than yesterday`;
                        return (
                          <Tippy
                            as="div"
                            className={`cursor-pointer ${tone} py-[3px] flex rounded-full text-white text-xs pl-2 pr-1 items-center font-medium`}
                            content={label}
                          >
                            {pct}%
                            <Lucide icon={i.isUp ? 'ChevronUp' : 'ChevronDown'} className="w-4 h-4 ml-0.5" />
                          </Tippy>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="mt-6 text-3xl font-medium leading-8">{data?.currencySymbol ? `${data.currencySymbol} ` : ''}{formatAmount(data?.totals.todayRevenue || 0)}</div>
                  <div className="mt-1 text-base text-slate-500">Total Revenue (Today)</div>
                  <div className="mt-1 text-xs text-slate-500">Yesterday: <span className="font-semibold text-slate-700">{data?.currencySymbol ? `${data.currencySymbol} ` : ''}{formatAmount(data?.totals.yesterdayRevenue || 0)}</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Today's orders by status - pie chart */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 intro-y">
              <div
                className={clsx([
                  'relative zoom-in',
                  "before:box before:absolute before:inset-x-3 before:mt-3 before:h-full before:bg-slate-50 before:content-['']",
                ])}
              >
                <div className="p-5 box">
                  <div className="text-sm font-semibold text-slate-800">Today's Orders by Status</div>
                  <div className="text-xs text-slate-500">Breakdown of orders created today across workflow statuses.</div>
                  <div className="mt-4 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                    <div className="md:w-1/2 flex items-center justify-center md:min-h-[260px]">
              {(() => {
                const statusOrder = [
                  { key: 'draft', label: 'Draft', color: getColor('pending', 0.9) },
                  { key: 'approved', label: 'Approved', color: getColor('info', 0.9) },
                  { key: 'confirmed', label: 'Confirmed', color: getColor('warning', 0.9) },
                  { key: 'in_progress', label: 'In progress', color: getColor('primary', 0.9) },
                  { key: 'completed', label: 'Completed', color: getColor('secondary', 0.9) },
                  { key: 'verified', label: 'Verified', color: getColor('success', 0.9) },
                  { key: 'delivered', label: 'Delivered', color: getColor('theme.1', 0.9) },
                  { key: 'cancelled', label: 'Cancelled', color: getColor('danger', 0.9) },
                ];
                const counts = (data as any)?.todayStatusCounts || {};
                const labels = statusOrder.map(s => s.label);
                const values = statusOrder.map(s => Number(counts[s.key]) || 0);
                const total = values.reduce((acc, v) => acc + v, 0);
                if (total === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center text-center text-slate-400">
                      <Lucide icon="Inbox" className="w-10 h-10 mb-2 text-slate-300" />
                      <p className="text-sm">No orders placed yet today.</p>
                    </div>
                  );
                }
                const colors = statusOrder.map(s => s.color);
                const chartData = {
                  labels,
                  datasets: [{
                    data: values,
                    backgroundColor: colors,
                    hoverBackgroundColor: colors,
                    borderWidth: 5,
                    borderColor: getColor('white'),
                  }],
                } as any;
                const options = { maintainAspectRatio: false, plugins: { legend: { display: false } } } as any;
                return <Chart type="pie" width="300" height="213" data={chartData} options={options} />;
              })()}
                    </div>
                    <div className="md:w-1/2 flex items-center justify-center md:min-h-[260px]">
                      <div className="w-64">
              {(() => {
                const statusOrder = [
                  { key: 'draft', label: 'Draft', className: 'bg-pending' },
                  { key: 'approved', label: 'Approved', className: 'bg-info' },
                  { key: 'confirmed', label: 'Confirmed', className: 'bg-warning' },
                  { key: 'in_progress', label: 'In progress', className: 'bg-primary' },
                  { key: 'completed', label: 'Completed', className: 'bg-secondary' },
                  { key: 'verified', label: 'Verified', className: 'bg-success' },
                  { key: 'delivered', label: 'Delivered', className: 'bg-theme-1' },
                  { key: 'cancelled', label: 'Cancelled', className: 'bg-danger' },
                ];
                const counts = (data as any)?.todayStatusCounts || {};
                const totalRaw = statusOrder.reduce((acc, s) => acc + (Number(counts[s.key]) || 0), 0);
                if (totalRaw === 0) {
                  return (
                    <div className="text-center text-slate-400 text-sm">
                      No orders placed yet today.
                    </div>
                  );
                }
                const total = totalRaw || 1;
                return statusOrder.map((s, idx) => {
                  const val = Number(counts[s.key]) || 0;
                  const pct = Math.round((val / total) * 100);
                  return (
                    <div key={s.key} className={`flex items-center ${idx ? 'mt-4' : ''}`}>
                      <div className={`w-2 h-2 mr-3 rounded-full ${s.className}`}></div>
                      <span className="truncate">{s.label}</span>
                      <span className="ml-auto font-medium">{pct}%</span>
                    </div>
                  );
                });
              })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Next due tables (stacked) */}
          <div className="grid grid-cols-12 gap-4">
            {/* Abandoned cart next due */}
            <div className="col-span-12 intro-y">
              <div className={clsx(['relative zoom-in', "before:box before:absolute before:inset-x-3 before:mt-3 before:h-full before:bg-slate-50 before:content-['']"])}>
                <div className="p-5 box">
                  <div className="text-sm font-semibold text-slate-800">Abandoned Cart - Upcoming Due</div>
                  <div className="text-xs text-slate-500">Reminders with due date not crossed yet.</div>
                  <div className="mt-3 overflow-auto">
                    <table className="min-w-full text-left text-sm text-slate-600">
                      <thead className="text-xs uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-2">Order #</th>
                          <th className="px-3 py-2">Customer</th>
                          <th className="px-3 py-2">Product</th>
                          <th className="px-3 py-2">Due Date</th>
                          <th className="px-3 py-2 text-right">Days Left</th>
                        </tr>
                      </thead>
                      <tbody>
                        {abandoned.length === 0 ? (
                          <tr><td className="px-3 py-4 text-slate-400" colSpan={4}>No upcoming reminders.</td></tr>
                        ) : abandoned.map((r) => (
                          <tr key={r.id} className="even:bg-slate-50/70 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedDue(r)}>
                            <td className="px-3 py-2 font-semibold">{r.orderNumber ?? '—'}</td>
                            <td className="px-3 py-2">{r.customerName ?? '—'}</td>
                            <td className="px-3 py-2">{r.productName ?? '—'}</td>
                            <td className="px-3 py-2">{new Date(r.dueDate).toLocaleDateString()}</td>
                            <td className="px-3 py-2 text-right">{r.daysLeft}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 flex justify-end gap-2 text-xs">
                    <button className="px-2 py-1 rounded border" disabled={abandonedPage<=1} onClick={()=>setAbandonedPage(p=>Math.max(1,p-1))}>Prev</button>
                    <span className="self-center">Page {abandonedPage} / {abandonedTotalPages}</span>
                    <button className="px-2 py-1 rounded border" disabled={abandonedPage>=abandonedTotalPages} onClick={()=>setAbandonedPage(p=>Math.min(abandonedTotalPages,p+1))}>Next</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Order items next due */}
            <div className="col-span-12 intro-y">
              <div className={clsx(['relative zoom-in', "before:box before:absolute before:inset-x-3 before:mt-3 before:h-full before:bg-slate-50 before:content-['']"])}>
                <div className="p-5 box">
                  <div className="text-sm font-semibold text-slate-800">Order Items - Upcoming Due</div>
                  <div className="text-xs text-slate-500">Order service/items due soon.</div>
                  <div className="mt-3 overflow-auto">
                    <table className="min-w-full text-left text-sm text-slate-600">
                      <thead className="text-xs uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-2">Order #</th>
                          <th className="px-3 py-2">Customer</th>
                          <th className="px-3 py-2">Item</th>
                          <th className="px-3 py-2">Due Date</th>
                          <th className="px-3 py-2 text-right">Days Left</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderItems.length === 0 ? (
                          <tr><td className="px-3 py-4 text-slate-400" colSpan={4}>No upcoming due items.</td></tr>
                        ) : orderItems.map((r) => (
                          <tr key={r.id} className="even:bg-slate-50/70 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedDue(r)}>
                            <td className="px-3 py-2 font-semibold">{r.orderNumber ?? '—'}</td>
                            <td className="px-3 py-2">{r.customerName ?? '—'}</td>
                            <td className="px-3 py-2">{r.productName ?? '—'}</td>
                            <td className="px-3 py-2">{new Date(r.dueDate).toLocaleDateString()}</td>
                            <td className="px-3 py-2 text-right">{r.daysLeft}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 flex justify-end gap-2 text-xs">
                    <button className="px-2 py-1 rounded border" disabled={orderItemsPage<=1} onClick={()=>setOrderItemsPage(p=>Math.max(1,p-1))}>Prev</button>
                    <span className="self-center">Page {orderItemsPage} / {orderItemsTotalPages}</span>
                    <button className="px-2 py-1 rounded border" disabled={orderItemsPage>=orderItemsTotalPages} onClick={()=>setOrderItemsPage(p=>Math.min(orderItemsTotalPages,p+1))}>Next</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Today's / Tomorrow's appointments */}
          <div className="grid grid-cols-12 gap-4 mt-4">
            {/* Today's appointments */}
            <div className="col-span-12 lg:col-span-6 intro-y">
              <div
                className={clsx([
                  'relative zoom-in',
                  "before:box before:absolute before:inset-x-3 before:mt-3 before:h-full before:bg-slate-50 before:content-['']",
                ])}
              >
                <div className="p-5 box">
                  <div className="text-sm font-semibold text-slate-800">
                    Today&apos;s Appointments
                  </div>
                  <div className="text-xs text-slate-500">
                    Appointments scheduled for today.
                  </div>
                  <div className="mt-3 overflow-auto">
                    <table className="min-w-full text-left text-sm text-slate-600">
                      <thead className="text-xs uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-2">Customer</th>
                          <th className="px-3 py-2">Time</th>
                          <th className="px-3 py-2">Vehicle</th>
                          <th className="px-3 py-2">Location</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {todayAppointments.length === 0 ? (
                          <tr>
                            <td
                              className="px-3 py-4 text-slate-400"
                              colSpan={4}
                            >
                              No appointments scheduled for today.
                            </td>
                          </tr>
                        ) : (
                          todayAppointments.map((a) => {
                            const customerName = a.customer
                              ? `${a.customer.firstName ?? ''} ${
                                  a.customer.lastName ?? ''
                                }`.trim() ||
                                a.customer.mobileNumber ||
                                a.customer.email ||
                                'Customer'
                              : 'Customer';
                            const timeLabel = new Date(
                              a.scheduledAt,
                            ).toLocaleTimeString(undefined, {
                              hour: '2-digit',
                              minute: '2-digit',
                            });
                            const vehicleLabel = a.vehicle
                              ? [
                                  a.vehicle.vehicleBrand?.name,
                                  a.vehicle.vehicleModel?.name,
                                  a.vehicle.vehicleRegistrationNumber,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')
                              : '—';
                            const locationLabel =
                              a.location?.name ?? a.locationId ?? '—';
                            return (
                              <tr
                                key={a.id}
                                className="even:bg-slate-50/70"
                              >
                                <td className="px-3 py-2">{customerName}</td>
                                <td className="px-3 py-2">{timeLabel}</td>
                                <td className="px-3 py-2">{vehicleLabel}</td>
                                <td className="px-3 py-2">
                                  {locationLabel}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <a
                                    href={`/appointments/${a.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary"
                                  >
                                    View
                                  </a>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 flex justify-end gap-2 text-xs">
                    <PaginationBar
                      page={todayAppointmentsPage}
                      totalPages={todayAppointmentsTotalPages}
                      onPrev={() =>
                        setTodayAppointmentsPage((p) => Math.max(1, p - 1))
                      }
                      onNext={() =>
                        setTodayAppointmentsPage((p) =>
                          Math.min(todayAppointmentsTotalPages, p + 1),
                        )
                      }
                      onSelect={(v) => setTodayAppointmentsPage(v)}
                      disabled={false}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Tomorrow's appointments */}
            <div className="col-span-12 lg:col-span-6 intro-y">
              <div
                className={clsx([
                  'relative zoom-in',
                  "before:box before:absolute before:inset-x-3 before:mt-3 before:h-full before:bg-slate-50 before:content-['']",
                ])}
              >
                <div className="p-5 box">
                  <div className="text-sm font-semibold text-slate-800">
                    Tomorrow&apos;s Appointments
                  </div>
                  <div className="text-xs text-slate-500">
                    Appointments scheduled for tomorrow.
                  </div>
                  <div className="mt-3 overflow-auto">
                    <table className="min-w-full text-left text-sm text-slate-600">
                      <thead className="text-xs uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-2">Customer</th>
                          <th className="px-3 py-2">Time</th>
                          <th className="px-3 py-2">Vehicle</th>
                          <th className="px-3 py-2">Location</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tomorrowAppointments.length === 0 ? (
                          <tr>
                            <td
                              className="px-3 py-4 text-slate-400"
                              colSpan={4}
                            >
                              No appointments scheduled for tomorrow.
                            </td>
                          </tr>
                        ) : (
                          tomorrowAppointments.map((a) => {
                            const customerName = a.customer
                              ? `${a.customer.firstName ?? ''} ${
                                  a.customer.lastName ?? ''
                                }`.trim() ||
                                a.customer.mobileNumber ||
                                a.customer.email ||
                                'Customer'
                              : 'Customer';
                            const timeLabel = new Date(
                              a.scheduledAt,
                            ).toLocaleTimeString(undefined, {
                              hour: '2-digit',
                              minute: '2-digit',
                            });
                            const vehicleLabel = a.vehicle
                              ? [
                                  a.vehicle.vehicleBrand?.name,
                                  a.vehicle.vehicleModel?.name,
                                  a.vehicle.vehicleRegistrationNumber,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')
                              : '—';
                            const locationLabel =
                              a.location?.name ?? a.locationId ?? '—';
                            return (
                              <tr
                                key={a.id}
                                className="even:bg-slate-50/70"
                              >
                                <td className="px-3 py-2">{customerName}</td>
                                <td className="px-3 py-2">{timeLabel}</td>
                                <td className="px-3 py-2">{vehicleLabel}</td>
                                <td className="px-3 py-2">
                                  {locationLabel}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <a
                                    href={`/appointments/${a.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary"
                                  >
                                    View
                                  </a>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 flex justify-end gap-2 text-xs">
                    <PaginationBar
                      page={tomorrowAppointmentsPage}
                      totalPages={tomorrowAppointmentsTotalPages}
                      onPrev={() =>
                        setTomorrowAppointmentsPage((p) =>
                          Math.max(1, p - 1),
                        )
                      }
                      onNext={() =>
                        setTomorrowAppointmentsPage((p) =>
                          Math.min(tomorrowAppointmentsTotalPages, p + 1),
                        )
                      }
                      onSelect={(v) => setTomorrowAppointmentsPage(v)}
                      disabled={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        {selectedDue && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-xl bg-white p-4 shadow-xl">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">Due Details</div>
                <button type="button" className="rounded border border-slate-300 bg-white px-2 py-1 text-sm hover:bg-slate-50" onClick={() => setSelectedDue(null)}>✕</button>
              </div>
              <div className="space-y-2 text-sm">
                <div><span className="text-slate-500">Customer:</span> <span className="font-medium text-slate-800">{selectedDue.customerName || '—'}</span></div>
                <div><span className="text-slate-500">Order #:</span> <span className="font-medium text-slate-800">{selectedDue.orderNumber || '—'}</span></div>
                <div><span className="text-slate-500">Item:</span> <span className="font-medium text-slate-800">{selectedDue.productName || '—'}</span></div>
                <div><span className="text-slate-500">Due Date:</span> <span className="font-medium text-slate-800">{new Date(selectedDue.dueDate).toLocaleString()}</span></div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <a href={`/order-management/${selectedDue.orderId}`} className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90">View Order</a>
                <button type="button" className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => setSelectedDue(null)}>Close</button>
              </div>
            </div>
          </div>
        )}
        </>
      )}
    </PageScaffold>
  );
};

export default Dashboard;
